import { forwardRef } from 'react';
import { deriveBarState } from '../utils/progressMath';

/**
 * Linear horizontal progress bar shown on the Chapters page while a book is
 * still being processed. Click / Enter / Space opens the detail overlay.
 *
 * Props:
 *   snapshot     — ProgressSnapshot from GET /files/{id}/progress (or null
 *                  before the first poll lands)
 *   chapters     — loaded chapter rows, optional; when given they cap the
 *                  percentage so lagging per-file counters cannot overstate it
 *   onOpenDetail — () => void; called when the bar is activated
 */
const ProcessingProgressBar = forwardRef(function ProcessingProgressBar(
  { snapshot, chapters, onOpenDetail },
  ref,
) {
  const { percent, currentStageLabel, appearance, failedStage } = deriveBarState(snapshot, chapters);
  const isError = appearance === 'error';

  const interactive = typeof onOpenDetail === 'function';

  const handleKeyDown = (e) => {
    if (!interactive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenDetail();
    }
  };

  const wrapperBg = isError
    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
    : 'bg-gray-100 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700';
  const fillBg = isError
    ? 'bg-red-500 dark:bg-red-600'
    : 'bg-primary';

  return (
    <div
      ref={ref}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? () => onOpenDetail() : undefined}
      onKeyDown={handleKeyDown}
      className={`mt-3 p-4 border rounded-lg ${interactive ? 'cursor-pointer hover:brightness-95' : ''} transition-colors ${wrapperBg}`}
      title={isError && failedStage?.error_message ? failedStage.error_message : undefined}
      data-testid="processing-progress-bar"
    >
      <div className="flex items-center justify-between mb-2">
        <span className={`text-sm font-medium ${isError ? 'text-red-800 dark:text-red-200' : 'text-gray-700 dark:text-gray-200'}`}>
          {currentStageLabel}
        </span>
        <span className={`text-sm font-mono tabular-nums ${isError ? 'text-red-800 dark:text-red-200' : 'text-gray-600 dark:text-gray-300'}`}>
          {percent}%
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Processing: ${currentStageLabel}`}
        className="w-full h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden"
      >
        <div
          className={`h-full ${fillBg} transition-all duration-500 ease-out`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
});

export default ProcessingProgressBar;
